/**
 * Node management orders (workbench D257, AIOE 0.37.0), in words. The wire shapes are the platform's
 * (`@aioe/shared` deviceOrders.ts); the console's reading of them lives here so no order id, kind
 * string or state word ever reaches a person.
 */

import { NODE_ORDER_KINDS, describeRunnerOrder, type DeviceFacts, type DeviceOrderKind, type DeviceOrderRecord, type DeviceOrderRecordState, fitBudgetBytes, type FitEstimate, fitForDevice, fitHuman, type FitVerdict, type RunnerConfigureBody, type RunnerFacts } from '@aioe/shared';

export type OrderKind = DeviceOrderKind;
export type OrderState = DeviceOrderRecordState;
export type DeviceOrder = DeviceOrderRecord;
export type { DeviceFacts, RunnerFacts };

export const factsOf = (device: unknown): DeviceFacts | undefined => (device as { facts?: DeviceFacts } | undefined)?.facts;

export const KIND_TEXT: Record<OrderKind, string> = {
  'model.download': 'Download a model',
  'model.remove': 'Remove a model',
  'runtime.install': 'Install a runtime',
  'runtime.remove': 'Remove a runtime',
  'dlp.configure': 'Configure DLP',
  'setting.set': 'Set a setting',
  'skill.evaluate': 'Evaluate a skill',
  'runner.configure': 'Configure the local model',
  'specialist.evaluate': 'Evaluate a specialist',
  'toolserver.verify': 'Verify a tool server',
  'foundry.publish': 'Publish a Foundry agent',
  'foundry.unpublish': 'Delete a Foundry agent',
  'teams.refresh': 'Refresh teams',
  'routine.trigger': 'Start a routine when something happens',
  'workflow.start': 'Start a workflow',
  'service.configure': 'Start at login',
  'update.configure': 'Updates',
  'routine.run': 'Run a routine',
  'routine.remove': 'Remove a routine',
  'remote.configure': 'Remote control of sessions',
  'node.setting.set': 'Set a node setting',
};

/** The kinds a person orders from a node or project menu; a skill is evaluated from the catalogue and a specialist from the Teams page instead. */
/** A node's own settings, service, updates and routines (D290) come from the node's Manage view, not this menu. */
export const MENU_KINDS = (Object.keys(KIND_TEXT) as OrderKind[]).filter((k) => k !== 'skill.evaluate' && k !== 'specialist.evaluate' && k !== 'toolserver.verify' && k !== 'foundry.publish' && k !== 'foundry.unpublish' && k !== 'teams.refresh' && k !== 'routine.trigger' && k !== 'workflow.start' && !(NODE_ORDER_KINDS as readonly string[]).includes(k));

export const STATE_TEXT: Record<OrderState, string> = { pending: 'Waiting', done: 'Done', failed: 'Failed', refused: 'Refused', expired: 'Expired' };
export const STATE_MARK: Record<OrderState, 'working' | 'online' | 'error' | 'offline'> = { pending: 'working', done: 'online', failed: 'error', refused: 'error', expired: 'offline' };

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);

export function bytesInWords(n: number | undefined): string | undefined {
  if (n === undefined || !Number.isFinite(n) || n <= 0) return undefined;
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

/** What an order is about, in a few words: the model's name, the runtime's, the setting's key, the DLP parts changed. */
export function orderSubject(o: Pick<DeviceOrder, 'kind' | 'body'>): string {
  const b = o.body;
  switch (o.kind) {
    case 'model.download':
    case 'model.remove':
      return str(b.name) ?? str(b.id) ?? str(b.file) ?? 'a model';
    case 'runtime.install':
    case 'runtime.remove':
      return str(b.name) ?? str(b.id) ?? 'a runtime';
    case 'setting.set':
      return `${str(b.key) ?? 'a setting'}${b.value === null ? ' (cleared)' : b.value === undefined ? '' : ` to ${typeof b.value === 'string' ? b.value : JSON.stringify(b.value)}`}`;
    case 'dlp.configure': {
      const n = Object.keys(b).length;
      return n ? `${n} ${n === 1 ? 'part' : 'parts'} of the data protection` : 'data protection';
    }
    case 'skill.evaluate': {
      const n = Array.isArray(b.tasks) ? b.tasks.length : 0;
      return `${str(b.name) ?? 'a skill'}${n ? ` on ${n} ${n === 1 ? 'task' : 'tasks'}` : ''}`;
    }
    case 'runner.configure':
      return describeRunnerOrder(b as RunnerConfigureBody);
    case 'specialist.evaluate':
      return `${str(b.specialistName) ?? 'a specialist'} against ${str(b.datasetName) ?? 'a dataset'}`;
    case 'toolserver.verify':
      return `${str(b.name) ?? 'a tool server'}, with no secrets`;
    case 'foundry.publish':
      return `the Foundry agent for ${str(b.memberId) ?? 'a member'}`;
    case 'foundry.unpublish':
      return `the Foundry agent ${str(b.agentName) ?? ''}`.trim();
    case 'teams.refresh': {
      const n = Array.isArray(b.teamIds) ? b.teamIds.length : 0;
      return n ? `${n === 1 ? '1 team' : `${n} teams`} from the organisation` : 'every team it holds, from the organisation';
    }
    case 'routine.trigger': {
      const e = b.event as { summary?: unknown } | undefined;
      return `${str(b.routineId) ?? 'a routine'}${str(e?.summary) ? ` for ${str(e?.summary)}` : ''}`;
    }
    case 'workflow.start': {
      const e = b.event as { summary?: unknown } | undefined;
      return `${str(b.workflowId) ?? 'a workflow'}${str(e?.summary) ? ` for ${str(e?.summary)}` : ''}`;
    }
    case 'service.configure':
      return b.startAtLogin ? 'on' : 'off';
    case 'update.configure':
      return [str(b.channel) ? `the ${str(b.channel)} channel` : undefined, typeof b.autoUpdate === 'boolean' ? `automatic ${b.autoUpdate ? 'on' : 'off'}` : undefined].filter(Boolean).join(', ') || 'unchanged';
    case 'routine.run':
    case 'routine.remove':
      return str(b.name) ?? str(b.id) ?? 'a routine';
    case 'remote.configure':
      return b.allSessions ? 'every session of the node’s person' : 'only sessions turned on one by one';
    case 'node.setting.set':
      return `${str(b.key) ?? 'a node setting'}${b.value === null ? ' (cleared)' : ` to ${typeof b.value === 'string' ? b.value : JSON.stringify(b.value)}`}`;
  }
}

/** "Download a model: Qwen 3 4B", for a row. */
export const orderLine = (o: Pick<DeviceOrder, 'kind' | 'body'>): string => `${KIND_TEXT[o.kind]}: ${orderSubject(o)}`;

/** The batch's summary: "3 of 4 done, 1 refused: <message>", "2 of 4 done, 2 waiting". */
export function batchSummary(orders: DeviceOrder[]): string {
  const count = (s: OrderState) => orders.filter((o) => o.state === s).length;
  const parts: string[] = [];
  for (const s of ['failed', 'refused', 'expired', 'pending'] as const) {
    const n = count(s);
    if (n) parts.push(`${n} ${STATE_TEXT[s].toLowerCase()}`);
  }
  const first = orders.find((o) => (o.state === 'refused' || o.state === 'failed') && o.message);
  return `${count('done')} of ${orders.length} done${parts.length ? `, ${parts.join(', ')}` : ''}${first ? `: ${first.message}` : ''}`;
}

/** The kind's one-sentence sentence for the audit and activity words. */
export function orderSentence(kind: string | undefined, what: string | undefined, state: string | undefined, message?: string): string {
  const k = (kind as OrderKind) in KIND_TEXT ? KIND_TEXT[kind as OrderKind].toLowerCase() : 'a change';
  const subject = what ? ` (${what})` : '';
  switch (state) {
    case 'done':
      return `A machine did what it was asked: ${k}${subject}.`;
    case 'failed':
      return `A machine could not do what it was asked: ${k}${subject}${message ? `. ${message}` : ''}.`;
    case 'refused':
      return `A machine refused to ${k}${subject}${message ? `: ${message}` : ''}.`;
    case 'expired':
      return `A machine did not pick up the request to ${k}${subject} in time.`;
    default:
      return `A machine was asked to ${k}${subject}.`;
  }
}

/** The DLP state in words: "Classifying with a local model · 3 repositories · managed by the organisation". */
export function dlpInWords(d: NonNullable<DeviceFacts['dlp']>): string {
  const parts: string[] = [];
  if (d.classifying === 0) parts.push('Not classifying');
  else parts.push(`Classifying with ${d.models.length === 1 ? d.models[0] : 'a local model'}${d.classifying < d.repositories ? ` in ${d.classifying} of them` : ''}`);
  parts.push(`${d.repositories} ${d.repositories === 1 ? 'repository' : 'repositories'}`);
  if (d.managed > 0) parts.push(d.managed >= d.repositories ? 'managed by the organisation' : `${d.managed} managed by the organisation`);
  return parts.join(' · ');
}

/** The machine's memory in one line: "64 GB RAM, NVIDIA RTX 4090 24 GB" or "32 GB unified memory, Apple M3". */
export function hardwareInWords(h: NonNullable<DeviceFacts['hardware']>): string {
  const gpus = h.gpus.map((g) => `${g.name}${!h.unified && g.vramBytes ? ` ${fitHuman(g.vramBytes)}` : ''}`);
  return [`${fitHuman(h.ramBytes)} ${h.unified ? 'unified memory' : 'RAM'}`, ...gpus].join(', ');
}

/** The estimate for a model on a machine that reported its hardware; undefined when either is not known. */
export function fitOn(model: { sizeBytes?: number; contextLength?: number }, hardware: DeviceFacts['hardware'] | undefined): FitEstimate | undefined {
  return hardware && model.sizeBytes ? fitForDevice(hardware, model.sizeBytes, model.contextLength) : undefined;
}

/** A fit verdict in a few words, beside a model's name in a list. */
export const FIT_WORDS: Record<FitVerdict, string> = {
  'fits-gpu': 'fits in GPU memory',
  'fits-ram': 'fits in memory',
  tight: 'tight fit',
  'too-big': 'too big for this machine',
  unknown: 'size unknown',
};

// ── The local model runner (D277) ─────────────────────────────────────

export type RunnerBackend = RunnerFacts['backends'][number];

/** A backend as a choice: what it runs on. */
export const BACKEND_TEXT: Record<RunnerBackend | 'auto', string> = { auto: 'Automatic', cpu: 'CPU only', cuda: 'CUDA (NVIDIA)', rocm: 'ROCm (AMD)', vulkan: 'Vulkan', metal: 'Metal (Apple)' };
const BACKEND_NAME: Record<RunnerBackend, string> = { cpu: 'the CPU', cuda: 'CUDA', rocm: 'ROCm', vulkan: 'Vulkan', metal: 'Metal' };

/** The GPUs a backend drives on this machine: CUDA the NVIDIA ones, ROCm the AMD ones, Metal Apple's, Vulkan any but Apple's, the CPU none. */
export function gpusFor(backend: RunnerBackend | 'auto' | undefined, r: RunnerFacts): RunnerFacts['gpus'] {
  const b = backend === 'auto' || backend === undefined ? r.backendInUse : backend;
  switch (b) {
    case 'cuda':
      return r.gpus.filter((g) => g.vendor === 'nvidia');
    case 'rocm':
      return r.gpus.filter((g) => g.vendor === 'amd');
    case 'metal':
      return r.gpus.filter((g) => g.vendor === 'apple');
    case 'vulkan':
      return r.gpus.filter((g) => g.vendor !== 'apple');
    case 'cpu':
      return [];
    default:
      return r.gpus;
  }
}

/** What the runner is doing, after its status word: "Qwen 2.5 3B on Vulkan (NVIDIA RTX A4000) with 8,192 context". */
export function runnerInWords(r: RunnerFacts): string {
  const backend = r.backendInUse ?? (r.backend === 'auto' ? undefined : r.backend);
  const gpu = r.gpuName ?? (r.gpu === 'all' ? 'every GPU' : r.gpu !== 'auto' ? r.gpu : undefined);
  const where = backend ? ` on ${BACKEND_NAME[backend]}${gpu && backend !== 'cpu' ? ` (${gpu})` : ''}` : ' with the automatic choice of backend';
  const context = r.settings.contextSize ? ` with ${r.settings.contextSize.toLocaleString('en-AU')} context` : '';
  return `${r.model?.name ?? 'No model chosen'}${where}${context}`;
}

/**
 * Before a model is ordered: which of these machines it fits, from what each reports ("Fits on 3 of 4 nodes;
 * too big for mju-gpu-2 (needs about 21 GB, has 16 GB)"). Undefined when the size is not known or no machine
 * reports its hardware. A tight fit still runs, so it counts as fitting.
 */
export function fitInWords(model: { sizeBytes?: number; contextLength?: number }, targets: Array<{ name: string; hardware?: DeviceFacts['hardware'] }>): string | undefined {
  if (!model.sizeBytes) return undefined;
  const known = targets.flatMap((t) => {
    const fit = fitOn(model, t.hardware);
    return fit ? [{ name: t.name, fit }] : [];
  });
  if (!known.length) return undefined;
  const big = known.filter((k) => k.fit.verdict === 'too-big');
  const tight = known.filter((k) => k.fit.verdict === 'tight');
  const ok = known.length - big.length;
  const where = targets.length === 1 ? targets[0]!.name : `${ok} of ${targets.length} nodes`;
  const parts = [big.length === known.length && targets.length === 1 ? '' : `Fits on ${where}`].filter(Boolean);
  if (big.length) parts.push(`too big for ${big.map((k) => `${k.name} (needs about ${fitHuman(k.fit.requiredBytes)}, has ${fitHuman(fitBudgetBytes(k.fit))})`).join(', ')}`);
  if (tight.length) parts.push(`tight on ${tight.map((k) => k.name).join(', ')}`);
  const unknown = targets.length - known.length;
  if (unknown) parts.push(`${unknown} ${unknown === 1 ? 'has' : 'have'} not reported its hardware`);
  const line = parts.join('; ');
  return line.charAt(0).toUpperCase() + line.slice(1);
}
